import { optimizedRetryOption } from "o4a-react";
import * as React from "react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { NetworkingDetails } from "../../components/NetworkingDetails/NetworkingDetails";
import { DetailsPanelId, PageId } from "../../constants/pluginConstants";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { AzureVirtualNetworkCollection, AzureVirtualNetworkSummary } from "../../gen/clients/mchub-azure-api-client";
import { CloudVmCluster } from "../../gen/clients/mchub-azure-api-client-exa";
import { parseId } from "../../helpers/idHelper";
import { responseItemstoArray } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { getOciRegion } from "../../utils";

export interface ExaVmClusterNetworkingDetailsProps {
  vmCluster: CloudVmCluster | undefined;
  location: string;
  subscriptionId: string;
  resourceGroupName: string,
}

export const ExaVmClusterNetworkingDetails = (
  { vmCluster, location, subscriptionId, resourceGroupName }: ExaVmClusterNetworkingDetailsProps,
): JSX.Element => {
  const { response: resNetwork } = useQueryCall({
    wait: !vmCluster?.networkLinkId,
    method: apiClients.withRegion(getOciRegion(location)).netLinkApi.getNetworkLink,
    options: {
      args: {
        networkLinkName: decodeURIComponent(parseId(vmCluster?.networkLinkId)?.resourceName || ""),
        subscriptionId,
        resourceGroupName,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadNetLink(),
      },
    },
  });

  const networkLink = resNetwork?.data;

  const { response: vnetResponse } = useQueryCall({
    wait: !(subscriptionId && location),
    method: apiClients.withRegion(getOciRegion(location)).azResourceApi.listAzureVirtualNetworksBySubscriptionOnly,
    options: {
      args: { subscriptionId, location },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadVNET(),
      },
    },
  });

  const vnets = (vnetResponse
    && responseItemstoArray<AzureVirtualNetworkCollection, AzureVirtualNetworkSummary>(vnetResponse)) || [];

  const selectedVnet = vnets?.find(vnet => vnet.id === networkLink?.properties?.azureAttachedNetworkIds?.[0]);

  return (
    <NetworkingDetails
      hostName={vmCluster?.hostname}
      vnet={selectedVnet}
      networkLink={networkLink}
      location={location}
      mcvcnId={vmCluster?.mcvcnId}
      analytics={{ pageId: PageId.VMCLUSTER_DETAILS, panelId: DetailsPanelId.NETWORKING }}
    />
  );
};
